import React from 'react';
import { User, Mail, Phone, Linkedin, Github, Globe, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
// Image supprimée - sera récupérée depuis l'API si nécessaire
// import profilImage from '../../cvtheques/vincent/profil.jpg';

const ProfileSection = ({ profile, cvService }) => {
  if (!profile) return null;

  const getContactIcon = (type) => {
    switch (type) {
      case 'email': return <Mail size={16} />;
      case 'phone': return <Phone size={16} />;
      case 'linkedin': return <Linkedin size={16} />;
      case 'github': return <Github size={16} />;
      case 'website': return <Globe size={16} />;
      default: return null;
    }
  };

  const formatContactLink = (type, value) => {
    switch (type) {
      case 'email':
        return `mailto:${value}`;
      case 'phone':
        return `tel:${value}`;
      default:
        return value.startsWith('http') ? value : `https://${value}`;
    }
  };

  return (
    <div className="mb-4 mt-4">
      <div className="backdrop-blur-md border border-hair shadow-xl overflow-hidden rounded-xl p-4">
        {/* Contact & Info Section - Style transparent */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column - Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-space-600 to-space-700 flex items-center justify-center mr-4 shadow-lg">
                  <User size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-ink">
                  {cvService.locale === 'fr' ? 'Informations de contact' : 'Contact Information'}
                </h3>
              </div>
              
              {/* Location */}
              {profile.location && (
                <div className="flex items-center text-ink-muted bg-space-800/70 backdrop-blur-sm px-3 py-2 rounded-lg border border-hair">
                  <Globe size={16} className="mr-2 text-ink" />
                  <div>
                    <span className="text-xs text-ink-dim block">
                      {cvService.locale === 'fr' ? 'Localisation' : 'Location'}
                    </span>
                    <span className="font-medium text-sm">{cvService.getTranslatedValue(profile.location)}</span>
                  </div>
                </div>
              )}
              
              {/* Email & Phone */}
              <div className="space-y-2">
                {profile.contact?.email && (
                  <div className="flex items-center text-ink-muted bg-space-800/70 backdrop-blur-sm px-3 py-2 rounded-lg border border-hair">
                    <Mail size={16} className="mr-2 text-ink" />
                    <div>
                      <span className="text-xs text-ink-dim block">Email</span>
                      <a 
                        href={`mailto:${profile.contact.email}`}
                        className="font-medium text-sm hover:text-ink transition-colors"
                      >
                        {profile.contact.email}
                      </a>
                    </div>
                  </div>
                )}
                
                {profile.contact?.phone && (
                  <div className="flex items-center text-ink-muted bg-space-800/70 backdrop-blur-sm px-3 py-2 rounded-lg border border-hair">
                    <Phone size={16} className="mr-2 text-ink" />
                    <div>
                      <span className="text-xs text-ink-dim block">
                        {cvService.locale === 'fr' ? 'Téléphone' : 'Phone'}
                      </span>
                      <a 
                        href={`tel:${profile.contact.phone}`}
                        className="font-medium text-sm hover:text-ink transition-colors"
                      >
                        {profile.contact.phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Column - Professional Links & Status */}
            <div className="space-y-3">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-space-600 to-space-700 flex items-center justify-center mr-4 shadow-lg">
                  <ExternalLink size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-ink">
                  {cvService.locale === 'fr' ? 'Liens professionnels' : 'Professional Links'}
                </h3>
              </div>
              
              {/* Professional Status */}
              <div className="bg-space-800/70 backdrop-blur-sm px-3 py-3 rounded-lg border border-hair">
                <div className="flex items-center mb-1">
                  <div className="w-2 h-2 bg-ink-dim rounded-full mr-2"></div>
                  <span className="text-xs text-ink-dim">
                    {cvService.locale === 'fr' ? 'Statut professionnel' : 'Professional Status'}
                  </span>
                </div>
                <p className="text-ink-muted font-medium text-sm">
                  {cvService.locale === 'fr' ? 'Ne se prononce pas' : 'Prefers not to say'}
                </p>
              </div>
              
              {/* Social Links */}
              <div className="space-y-2">
                {profile.contact?.linkedin && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start bg-space-800/70 hover:bg-space-700/70 border-hair text-ink-muted hover:text-ink transition-all duration-200 h-8"
                    asChild
                  >
                    <a 
                      href={formatContactLink('linkedin', profile.contact.linkedin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2"
                    >
                      <Linkedin size={14} />
                      <span className="text-sm">LinkedIn</span>
                      <ExternalLink size={12} className="ml-auto" />
                    </a>
                  </Button>
                )}
                
                {profile.contact?.github && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start bg-space-800/70 hover:bg-space-700/70 border-hair text-ink-muted hover:text-ink transition-all duration-200 h-8"
                    asChild
                  >
                    <a 
                      href={formatContactLink('github', profile.contact.github)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2"
                    >
                      <Github size={14} />
                      <span className="text-sm">GitHub</span>
                      <ExternalLink size={12} className="ml-auto" />
                    </a>
                  </Button>
                )}

                  {profile.contact?.malt && (
                      <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start bg-space-800/70 hover:bg-space-700/70 border-hair text-ink-muted hover:text-ink transition-all duration-200 h-8"
                          asChild
                      >
                          <a
                              href={formatContactLink('github', profile.contact.malt)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-2"
                          >
                              <Github size={14} />
                              <span className="text-sm">Malt</span>
                              <ExternalLink size={12} className="ml-auto" />
                          </a>
                      </Button>
                  )}
                
                {profile.contact?.website && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start bg-space-800/70 hover:bg-space-700/70 border-hair text-ink-muted hover:text-ink transition-all duration-200 h-8"
                    asChild
                  >
                    <a 
                      href={formatContactLink('website', profile.contact.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2"
                    >
                      <Globe size={14} />
                      {/* Les autres boutons portent un nom de marque qui se suffit (LinkedIn,
                          GitHub, Malt). « Website » ne dit rien : nommer ce qu'on trouve
                          derrière, dans la langue du lecteur. */}
                      <span className="text-sm">
                        {cvService.locale === 'fr' ? 'Endurance — agent IA' : 'Endurance — AI agent'}
                      </span>
                      <ExternalLink size={12} className="ml-auto" />
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Summary Section - Intégrée et compacte */}
          {profile.summary && (
            <div className="mt-4 pt-4 border-t border-hair">
              <div className="bg-space-800/70 p-4 rounded-lg border border-hair">
                <h3 className="text-base font-semibold mb-3 text-ink-muted flex items-center">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-space-600 to-space-700 flex items-center justify-center mr-4 shadow-lg">
                    <User size={20} className="text-white" />
                  </div>
                  {cvService.locale === 'fr' ? 'À propos' : 'About'}
                </h3>
                <p className="text-ink leading-relaxed text-base">
                  {cvService.getTranslatedValue(profile.summary)}
                </p>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default ProfileSection;
